import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Dialog } from '../../components/Dialog.tsx';
import { Icon } from '../../components/Icon.tsx';
import { useDeleteIssue } from './api.ts';
import { zoneErrorMessage } from './errors.ts';
import type { IssueDetail } from './types.ts';

/** Caller decides visibility (creator or org admin); the server remains the authority. */
export function DeleteIssueButton({ issue }: { issue: IssueDetail }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const del = useDeleteIssue();
  const navigate = useNavigate();
  const close = (): void => { setOpen(false); setError(null); };

  const confirm = (): void => {
    setError(null);
    del.mutate({ id: issue.id }, {
      onSuccess: () => { void navigate({ to: '/zone' }); },
      onError: (err) => setError(zoneErrorMessage(err)),
    });
  };

  return (
    <>
      <button type="button" className="btn-danger btn-sm" onClick={() => setOpen(true)}>
        <Icon name="delete" size={18} /> Delete
      </button>
      <Dialog open={open} onClose={close} title="Delete this issue?">
        <div className="space-y-5">
          <p className="text-sm leading-6 text-muted-foreground">
            <span className="font-medium text-foreground">#{issue.number} {issue.title}</span> and all of its photos,
            comments and activity will be permanently deleted. This cannot be undone.
          </p>
          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={close}>Cancel</button>
            <button
              type="button"
              className="btn-primary bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-foreground))] hover:bg-[hsl(var(--destructive)/0.9)]"
              disabled={del.isPending}
              onClick={confirm}
            >
              {del.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
